import { useState } from "react";
import { format, addMinutes, addDays, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CalendarDays, Clock } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const parseLocal = (s) => {
  if (!s) return new Date();
  const [dd, tt] = String(s).split("T");
  const [y, m, d] = dd.split("-").map(Number);
  const [h, mi] = (tt || "00:00").split(":").map(Number);
  return new Date(y, m - 1, d, h || 0, mi || 0);
};
const slots = (a = 7, b = 20) => { const r = []; for (let h = a; h <= b; h++) { r.push(`${pad(h)}:00`); if (h < b) r.push(`${pad(h)}:30`); } return r; };

// Sélecteur date/heure visuel (calendrier + créneaux) — remplace le natif Android.
// Utilisé à l'identique en création et modification de RDV.
export default function AppointmentDateTime({ start, end, allDay, durationMin = 0, workStart = 7, workEnd = 20, openHour = 9, closeHour = 18, onChange }) {
  const s = parseLocal(start);
  const e = parseLocal(end);
  const [endEdited, setEndEdited] = useState(false);
  const times = slots(Math.min(Number(workStart) || 7, Math.floor(openHour)), Math.max(Number(workEnd) || 20, Math.ceil(closeHour)));
  const emit = (ns, ne) => onChange({ start: toLocal(ns), end: toLocal(ne) });
  const at = (d, h) => { const x = startOfDay(d); x.setHours(Math.floor(h), Math.round((h % 1) * 60)); return x; };
  // anciens RDV « journée » : fin exclusive à minuit le lendemain
  const lastDay = (e.getHours() === 0 && e.getMinutes() === 0 && startOfDay(e) > startOfDay(s)) ? addDays(startOfDay(e), -1) : startOfDay(e);

  const setStartDate = (date) => {
    if (allDay) {
      const ns = at(date, openHour);
      const endDay = lastDay < startOfDay(date) ? date : lastDay;
      emit(ns, at(endDay, closeHour));
    } else {
      const ns = new Date(date.getFullYear(), date.getMonth(), date.getDate(), s.getHours(), s.getMinutes());
      let ne = new Date(date.getFullYear(), date.getMonth(), date.getDate(), e.getHours(), e.getMinutes());
      if (ne <= ns) ne = addMinutes(ns, durationMin || 60);
      emit(ns, ne);
    }
  };
  const setEndDate = (date) => {
    const ns = at(s, openHour);
    const endDay = startOfDay(date) < startOfDay(s) ? s : date;
    emit(ns, at(endDay, closeHour));
  };
  const setStartTime = (t) => {
    const [h, mi] = t.split(":").map(Number);
    const ns = new Date(s.getFullYear(), s.getMonth(), s.getDate(), h, mi);
    let ne = e;
    if (!endEdited && durationMin > 0) ne = addMinutes(ns, durationMin);
    else if (ne <= ns) ne = addMinutes(ns, durationMin || 60);
    emit(ns, ne);
  };
  const setEndTime = (t) => {
    setEndEdited(true);
    const [h, mi] = t.split(":").map(Number);
    let ne = new Date(s.getFullYear(), s.getMonth(), s.getDate(), h, mi);
    if (ne <= s) ne = addMinutes(ne, 0);
    emit(s, ne);
  };

  const displayEnd = allDay ? (lastDay < startOfDay(s) ? startOfDay(s) : lastDay) : e;
  const startTime = `${pad(s.getHours())}:${pad(s.getMinutes())}`;
  const endTime = `${pad(e.getHours())}:${pad(e.getMinutes())}`;

  return (
    <div className="space-y-3" data-testid="appt-datetime">
      <div className={allDay ? "grid grid-cols-2 gap-3" : ""}>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">{allDay ? "Date de début" : "Date"}</Label>
          <DatePop value={s} onSelect={setStartDate} testid="dt-start-date" />
        </div>
        {allDay && (
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Date de fin</Label>
            <DatePop value={displayEnd} onSelect={setEndDate} testid="dt-end-date" fromDate={s} />
          </div>
        )}
      </div>
      {allDay && (
        <p className="text-[11px] text-slate-500" data-testid="dt-allday-hours">Journée de travail : {pad(Math.floor(openHour))}:{pad(Math.round((openHour % 1) * 60))} → {pad(Math.floor(closeHour))}:{pad(Math.round((closeHour % 1) * 60))} (horaires de l'entreprise)</p>
      )}
      {!allDay && (
        <div className="grid grid-cols-2 gap-3">
          <TimeField label="Heure de début" value={startTime} times={times} onPick={setStartTime} testid="dt-start-time" />
          <TimeField label={`Heure de fin${durationMin > 0 && !endEdited ? " (auto)" : ""}`} value={endTime} times={times} onPick={setEndTime} testid="dt-end-time" />
        </div>
      )}
    </div>
  );
}

function DatePop({ value, onSelect, testid, fromDate }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 hover:border-slate-300 capitalize">
          <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />{format(value, "EEE d MMM yyyy", { locale: fr })}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-slate-200" align="start">
        <Calendar mode="single" selected={value} onSelect={(d) => { if (d) { onSelect(d); setOpen(false); } }} locale={fr} weekStartsOn={1} fromDate={fromDate} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function TimeField({ label, value, times, onPick, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid} className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 hover:border-slate-300">
            <Clock className="w-4 h-4 text-slate-400 shrink-0" />{value}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2 bg-white border-slate-200" align="start">
          <div className="grid grid-cols-3 gap-1 max-h-56 overflow-y-auto">
            {times.map((t) => (
              <button key={t} type="button" data-testid={`${testid}-${t}`} onClick={() => { onPick(t); setOpen(false); }}
                className={`text-xs py-1.5 rounded border transition ${t === value ? "bg-emerald-500 text-black border-emerald-500 font-semibold" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-emerald-300"}`}>{t}</button>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100">
            <Input type="time" defaultValue={value} onChange={(ev) => ev.target.value && onPick(ev.target.value)} data-testid={`${testid}-manual`} className="h-8 bg-slate-50 border-slate-200 text-sm" />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
